import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../config";
import "./StravaCallback.css";

const StravaCallback = () => {
  const [searchParams] = useSearchParams();
  const [processed, setProcessed] = useState(false);
  const [statusMessage, setStatusMessage] = useState("Connecting to Strava…");
  const [tone, setTone] = useState("info");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const redirectTarget = useMemo(() => "/dashboard", []);

  useEffect(() => {
    if (processed) return;

    const exchangeToken = async () => {
      const code = searchParams.get("code");
      const error = searchParams.get("error");

      if (error) {
        setTone("error");
        setLoading(false);
        setStatusMessage(`Strava authorization failed: ${error}`);
        setTimeout(() => navigate(redirectTarget), 3000);
        return;
      }

      if (!code) {
        setTone("error");
        setLoading(false);
        setStatusMessage("No authorization code received from Strava.");
        setTimeout(() => navigate(redirectTarget), 3000);
        return;
      }

      try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        if (!user?.email) {
          setTone("error");
          setLoading(false);
          setStatusMessage("Please login before connecting Strava.");
          setTimeout(() => navigate("/login"), 3000);
          return;
        }

        setProcessed(true);
        const response = await axios.post(
          `${API_BASE_URL}/strava/exchange-token`,
          { code, email: user.email },
          { withCredentials: true }
        );

        const syncJobId = response.data?.sync_job_id;
        if (syncJobId) {
          localStorage.setItem("stravaAutoSyncJobId", syncJobId);
        }

        setTone("success");
        setLoading(false);
        setStatusMessage(
          syncJobId
            ? "Strava connected! Your runs are syncing automatically…"
            : "Strava connected! Fetching your latest runs…"
        );
        localStorage.setItem("stravaSyncPrompt", "sync");
        setTimeout(() => navigate(redirectTarget), 2000);
      } catch (error) {
        console.error("Error exchanging token:", error);
        setProcessed(false);
        setTone("error");
        setLoading(false);
        setStatusMessage(
          `Failed to connect to Strava: ${
            error.response?.data?.error || error.message
          }`
        );
        setTimeout(() => navigate(redirectTarget), 3000);
      }
    };

    exchangeToken();
  }, [processed, redirectTarget, navigate, searchParams]);

  return (
    <div className="strava-callback-container">
      <div className={`strava-callback-card ${tone}`}>
        <div className="strava-callback-header">
          <h1>Strava Authentication</h1>
          <p>We’re finishing the connection with Strava.</p>
        </div>

        <div className="strava-callback-body">
          {loading ? (
            <div className="strava-callback-loading" aria-hidden="true" />
          ) : (
            <div className="strava-callback-icon" aria-hidden="true">
              {tone === "success" ? "🎉" : tone === "error" ? "⚠️" : "🚴"}
            </div>
          )}
          <p>{statusMessage}</p>
        </div>

        <div className="strava-callback-footer">
          <p>You’ll be redirected automatically.</p>
          <button
            type="button"
            className="callback-button"
            onClick={() => navigate(redirectTarget)}
          >
            Return to dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default StravaCallback;
